// libs
import React, { memo, useCallback, useEffect, useMemo } from "react";
import { SerializedStyles } from "@emotion/core";
import { withRouter, RouteComponentProps } from "react-router";

// global components
import WithStyles from "shared/components/WithStyles";
import Portal from "shared/components/Portal";

// helpers
// TODO - move to common use helpers
import { useNavHelper } from "shared/components/Header/MobileHeader/helpers";
import { hideOverflowY, resetOverflowY } from "shared/helpers/dom";

// contexts
import OverlaySheetContext from "./OverlaySheetContext";

// constants
import { SHEET_TRANSITION } from "./constants";

// styles
import { OVERLAY_CSS } from "shared/styles/base";
import {
    sheetCss,
    sheetTopIn,
    sheetTopOut,
    sheetRightOut,
    sheetRightIn,
    sheetBottomIn,
    sheetBottomOut,
    sheetLeftIn,
    sheetLeftOut,
} from "./styles";

const SHEET_STYLES = {
    [SHEET_TRANSITION.TOP]: [sheetTopIn, sheetTopOut],
    [SHEET_TRANSITION.RIGHT]: [sheetRightIn, sheetRightOut],
    [SHEET_TRANSITION.BOTTOM]: [sheetBottomIn, sheetBottomOut],
    [SHEET_TRANSITION.LEFT]: [sheetLeftIn, sheetLeftOut],
};

interface IOverlaySheetProps extends RouteComponentProps {
    children: React.ReactNode;
    onHide: VoidFunction;
    navKey: string;
    transitionFrom: SHEET_TRANSITION;
    cssProp?: SerializedStyles | SerializedStyles[];
}

const EMPTY_FUNC = () => {
    /** DO NOTHING */
};

function OverlaySheet({
    children,
    navKey,
    transitionFrom,
    onHide = EMPTY_FUNC,
    history,
    cssProp,
}: IOverlaySheetProps) {
    const [hide, onHideSheet] = useNavHelper(history, navKey);

    useEffect(() => {
        hideOverflowY("body", false);
        return resetOverflowY;
    }, []);

    const [sheetInCss, sheetOutCss] = SHEET_STYLES[transitionFrom];

    const onAnimationEnd = useCallback(() => {
        if (hide === false) {
            return;
        }

        onHide();
        resetOverflowY();
    }, [hide, onHide]);

    const bottomSheetCss = useMemo(
        () => [
            sheetCss,
            hide && sheetOutCss,
            hide === false && sheetInCss,
            cssProp !== undefined && cssProp,
        ],
        [cssProp, hide, sheetInCss, sheetOutCss]
    );

    return (
        <Portal>
            <OverlaySheetContext.Provider value={{ doHide: onHideSheet }}>
                <WithStyles cssProps={OVERLAY_CSS} onClick={onHideSheet} data-test="os-overlay" />
                <WithStyles
                    cssProps={bottomSheetCss}
                    onAnimationEnd={onAnimationEnd}
                    data-test="os-content"
                >
                    {children}
                </WithStyles>
            </OverlaySheetContext.Provider>
        </Portal>
    );
}

export default withRouter(memo(OverlaySheet));
